// src/admin/plans/PlansTable.jsx — the view-only plans table (v223): one AdminTable per family, families in the
// order given (plansModel.groupPlans). No row click, no edit affordance. Price without currency (the read carries
// none); caps are the PLAN's defaults (a company may override them). No features column: the features are shown
// only in the families × features grid (PlanFeaturesGrid).
import React from 'react';
import { useTranslation } from 'react-i18next';
import AdminTable from '../components/AdminTable.jsx';
import { formatCredits, formatPrice } from '../../utils/creditsFormat.js';
import { familyLabelKey, periodCopy } from './plansModel.js';
import { COLORS, FONT_MONO, SEMANTIC } from '../../constants/theme.js';

const muted = { color: COLORS.textSecondary };

export function familyLabel(t, family) {
  const key = familyLabelKey(family);
  return key ? t(key, { defaultValue: family }) : String(family);
}

function columnsFor(t) {
  const unlimited = <span style={muted}>{t('plans.values.unlimited')}</span>;
  return [
    { key: 'display_name', label: t('plans.columns.name') },
    { key: 'id', label: t('plans.columns.id'), width: '170px', render: (p) => (
      <code style={{ fontFamily: FONT_MONO, fontSize: '12px', ...muted }}>{p.id}</code>
    ) },
    { key: 'price', label: t('plans.columns.price'), width: '120px', render: (p) => (
      p.price == null ? <span style={muted}>{t('plans.values.notSold')}</span> : (formatPrice(p.price) ?? '—')
    ) },
    { key: 'allowance_credits', label: t('plans.columns.allowance'), width: '130px', render: (p) => (
      p.enforces_limit === false ? unlimited : formatCredits(p.allowance_credits)
    ) },
    { key: 'period_days', label: t('plans.columns.period'), width: '160px', render: (p) => {
      const { key, params } = periodCopy(p);
      return t(key, params);
    } },
    { key: 'max_tenants', label: <span title={t('plans.capsHint')}>{t('plans.columns.workspaces')}</span>, width: '110px',
      render: (p) => (p.max_tenants == null ? unlimited : p.max_tenants) },
    { key: 'max_users', label: <span title={t('plans.capsHint')}>{t('plans.columns.users')}</span>, width: '100px',
      render: (p) => (p.max_users == null ? unlimited : p.max_users) },
    { key: 'self_serve', label: <span title={t('plans.selfServeHint')}>{t('plans.columns.selfServe')}</span>, width: '100px',
      render: (p) => (p.self_serve ? t('plans.values.yes') : <span style={muted}>{t('plans.values.no')}</span>) },
    { key: 'active', label: t('plans.columns.status'), width: '100px', render: (p) => (
      <span style={{ color: p.active ? SEMANTIC.verde.fg : SEMANTIC.errore.fg, fontSize: '12px' }}>
        {p.active ? t('plans.values.active') : t('plans.values.inactive')}
      </span>
    ) },
  ];
}

export default function PlansTable({ groups }) {
  const { t } = useTranslation('admin');
  const columns = columnsFor(t);
  if (!groups.length) {
    return <AdminTable columns={columns} rows={[]} loading={false} emptyMessage={t('plans.empty')} getRowKey={(p) => p.id} />;
  }
  return groups.map(({ family, plans }) => (
    <section key={family ?? 'all'} style={{ marginBottom: '24px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 8px', color: COLORS.ink }}>
        {family == null ? t('plans.allPlans') : familyLabel(t, family)}
      </h2>
      <AdminTable columns={columns} rows={plans} loading={false} getRowKey={(p) => p.id} />
    </section>
  ));
}
